import { useCallback, useEffect, type ReactElement } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';

import { useTopHeader } from 'app/hooks';
import { useTrackTabLoaded } from 'app/hooks/useTrackTabLoaded';
import { TAB_LOADED_EVENTS } from 'app/tracking/constants';
import { Tabs } from 'app/shared-components/structure';
import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import { setHeaderActions, resetHeaderActions } from 'app/shared-components/store/headerSlice';

import NewCampaignIcon from 'app/assets/icons/new_campaign.svg?react';
import SyncIcon from 'app/assets/icons/cycle.svg?react';
import FuseSuspense from '@/@fuse/core/FuseSuspense';

import SmsCampaignsOnboarding from '../use-cases/Onboarding/SmsCampaignsOnboarding';

const tabs = [
  { label: 'Campaigns', value: 'campaigns' },
  { label: 'Audience', value: 'audience' },
];

const SmsCampaignsLayout = (): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const selectedTab = pathname.endsWith('/audience') ? 'audience' : 'campaigns';

  useTopHeader('SMS campaigns');
  useTrackTabLoaded(TAB_LOADED_EVENTS.SMS_CAMPAIGNS);

  useEffect(() => {
    dispatch(
      setHeaderActions([
        {
          name: 'sync-audience',
          title: 'Sync audience',
          type: 'button',
          callback: () => navigate('/sms-campaigns/sync-audience'),
          icon: <SyncIcon width="16" height="16" />,
        },
        {
          name: 'new-campaign',
          title: 'Create new campaign',
          type: 'button',
          callback: () => navigate('/sms-campaigns/new-campaign'),
          className: 'text-featured',
          icon: <NewCampaignIcon width="20" height="20" />,
        },
      ])
    );

    return () => {
      dispatch(resetHeaderActions());
    };
  }, [dispatch, navigate]);

  const handleTabChange = useCallback(
    (_e: unknown, val: string) =>
      navigate(val === 'campaigns' ? '/sms-campaigns' : `/sms-campaigns/${val}`),
    [navigate]
  );

  return (
    <>
      <MarketingStationLayout>
        <Box className="flex items-center">
          <Tabs
            tabs={tabs}
            selectedTab={selectedTab}
            handleChange={handleTabChange}
            sx={{
              '& .MuiTab-root.Mui-selected': { color: 'black' },
              '& .MuiTabs-indicator': { backgroundColor: 'black' },
            }}
          />
        </Box>
        <Box className="grow bg-white flex flex-col overflow-y-auto rounded-8">
          <FuseSuspense>
            <Outlet />
          </FuseSuspense>
        </Box>
      </MarketingStationLayout>
      <SmsCampaignsOnboarding />
    </>
  );
};

export default SmsCampaignsLayout;
